import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Icon from '../AppIcon';
import NotificationCenter from './NotificationCenter';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProjectMenuCollapsed, setIsProjectMenuCollapsed] = useState(false);
  const location = useLocation();
  const params = useParams();
  
  // Check if we're in a project context
  const projectId = params?.projectId;
  const isProjectContext = !!projectId;

  // Auto-collapse main menu when in project routes
  useEffect(() => {
    if (location?.pathname?.startsWith('/project/')) {
      setIsCollapsed(true);
    }
  }, [location?.pathname]);

  // Update CSS variable for sidebar width
  useEffect(() => {
    document.documentElement?.style?.setProperty(
      '--sidebar-width',
      isCollapsed ? '80px' : '240px'
    );
  }, [isCollapsed]);

  // Update CSS variable for project submenu width
  useEffect(() => {
    document.documentElement?.style?.setProperty(
      '--project-submenu-width',
      isProjectMenuCollapsed ? '60px' : '192px'
    );
  }, [isProjectMenuCollapsed]);

  // Main menu structure
  const navigationSections = [
    {
      title: 'Projects',
      items: [
        {
          path: '/project-landing-dashboard',
          label: 'Project Dashboard',
          icon: 'FolderKanban'
        }
      ]
    },
    {
      title: 'Workflow Designer',
      items: [
        {
          path: '/visual-workflow-designer',
          label: 'Visual Designer',
          icon: 'Workflow'
        }
      ]
    },
    {
      title: 'Administration',
      items: [
        {
          path: '/system-configuration-and-administration',
          label: 'Systems Config',
          icon: 'Settings'
        },
        {
          path: '/client-management-database',
          label: 'Client Database',
          icon: 'Users'
        },
        {
          path: '/material-management-database',
          label: 'Material Database',
          icon: 'Package'
        }
      ]
    }
  ];

  // Project-specific sub-menu (shown when project is selected)
  const projectSubMenu = [
    {
      path: `/project/${projectId}/dashboard`,
      label: 'Project Dashboard',
      icon: 'LayoutDashboard'
    },
    {
      path: `/project/${projectId}/live-monitoring`,
      label: 'Live Monitoring',
      icon: 'Activity'
    },
    {
      path: `/project/${projectId}/dependencies`,
      label: 'Dependencies',
      icon: 'GitBranch'
    },
    {
      path: `/project/${projectId}/line-dashboard`,
      label: 'Line Dashboard',
      icon: 'LayoutDashboard'
    },
    {
      path: `/project/${projectId}/inspections`,
      label: 'Inspections',
      icon: 'ClipboardCheck'
    },
    {
      path: `/project/${projectId}/shift-scheduling`,
      label: 'Shift Scheduling',
      icon: 'Calendar'
    }
  ];

  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMobileMenu = () => {
    setIsOpen(false);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleProjectMenuCollapse = () => {
    setIsProjectMenuCollapsed(!isProjectMenuCollapsed);
  };

  return (
    <>
      <button
        className="mobile-menu-button"
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation menu"
      >
        <Icon name={isOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Factory" size={24} color="var(--color-primary)" />
          </div>
          <span className="sidebar-logo-text">ModularFlow</span>
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={toggleCollapse}
          className="sidebar-collapse-toggle"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
        </button>

        {/* Notification Center in Sidebar */}
        <div className="px-4 py-2">
          <NotificationCenter inSidebar={true} />
        </div>

        <nav className="sidebar-nav">
          {navigationSections?.map((section, sectionIndex) => (
            <div key={sectionIndex} className="sidebar-nav-section">
              <h3 className="sidebar-nav-section-title">{section?.title}</h3>
              {section?.items?.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item?.path}
                  className={`sidebar-nav-item ${location?.pathname === item?.path ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="sidebar-nav-item-icon">
                    <Icon name={item?.icon} size={20} />
                  </span>
                  <span className="sidebar-nav-item-text">{item?.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <NavigationStatusIndicator isCollapsed={isCollapsed} />
          <UserContextPanel isCollapsed={isCollapsed} />
        </div>
      </aside>

      {/* Project-specific sub-menu (right side) */}
      {isProjectContext && (
        <aside className={`project-submenu ${isProjectMenuCollapsed ? 'collapsed' : ''}`}>
          <div className="project-submenu-header">
            <Icon name="Folder" size={20} color="var(--color-primary)" />
            <span className="text-sm font-semibold project-submenu-header-text">Project Menu</span>
          </div>

          {/* Collapse Toggle Button for Project Menu */}
          <button
            onClick={toggleProjectMenuCollapse}
            className="project-submenu-collapse-toggle"
            aria-label={isProjectMenuCollapsed ? 'Expand project menu' : 'Collapse project menu'}
          >
            <Icon name={isProjectMenuCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={18} />
          </button>

          <nav className="project-submenu-nav">
            {projectSubMenu?.map((item, index) => (
              <Link
                key={index}
                to={item?.path}
                className={`project-submenu-item ${location?.pathname === item?.path ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <span className="project-submenu-item-icon">
                  <Icon name={item?.icon} size={18} />
                </span>
                <span className="project-submenu-item-text">{item?.label}</span>
              </Link>
            ))}
          </nav>
        </aside>
      )}
    </>
  );
};

const NavigationStatusIndicator = ({ isCollapsed }) => {
  const [systemStatus] = useState({
    erp: 'connected',
    mes: 'connected',
    equipment: 'warning'
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'var(--color-success)';
      case 'warning':
        return 'var(--color-warning)';
      case 'error':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle2';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className={`flex ${isCollapsed ? 'flex-col' : ''} items-center gap-2 px-3 py-2 rounded-xl bg-muted`}>
      <div className="flex items-center gap-2">
        <Icon
          name={getStatusIcon(systemStatus?.erp)}
          size={16}
          color={getStatusColor(systemStatus?.erp)}
        />
        {!isCollapsed && <span className="caption text-muted-foreground">ERP</span>}
      </div>
      <div className="flex items-center gap-2">
        <Icon
          name={getStatusIcon(systemStatus?.mes)}
          size={16}
          color={getStatusColor(systemStatus?.mes)}
        />
        {!isCollapsed && <span className="caption text-muted-foreground">MES</span>}
      </div>
      <div className="flex items-center gap-2">
        <Icon
          name={getStatusIcon(systemStatus?.equipment)}
          size={16}
          color={getStatusColor(systemStatus?.equipment)}
        />
        {!isCollapsed && <span className="caption text-muted-foreground">EQP</span>}
      </div>
    </div>
  );
};

const UserContextPanel = ({ isCollapsed }) => {
  const [userContext] = useState({
    name: 'Sarah Chen',
    role: 'Production Manager',
    shift: 'Day Shift',
    department: 'Assembly Line 3'
  });

  return (
    <div className="flex items-center gap-3 px-3 py-3 mt-4 rounded-xl bg-muted">
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-semibold">
        {userContext?.name?.split(' ')?.map(n => n?.[0])?.join('')}
      </div>
      {!isCollapsed && (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{userContext?.name}</p>
          <p className="caption text-muted-foreground truncate">{userContext?.role}</p>
        </div>
      )}
    </div>
  );
};

export default Sidebar;